/**
 * Los estados de interacción de un estilo global: hover, presionado y enfocado,
 * y la transición entre ellos.
 *
 * ── Viven en el estilo, no en cada elemento ──────────────────────────────────
 * Como una clase de Webflow: «Botón principal» dice cómo reacciona al ratón y
 * todos los botones con ese estilo lo heredan. Un elemento puede añadir o
 * pisar propiedades de un estado con sus propios `states`, que se mezclan
 * encima (ver `mergeInteractionStates`).
 *
 * ── Valores por defecto sin migrar nada ──────────────────────────────────────
 * Un estilo SIN el campo `states` recibe los de su papel (`defaultButtonStates`)
 * al pintarse; no se escribe nada en los proyectos. Uno con `states: {}` dice
 * «ninguno» a propósito y se respeta. Así los sitios que ya existen ganan un
 * hover correcto al volver a publicar, y quien lo quite lo tiene quitado.
 *
 * ── Ligados al propio botón, no a un color fijo ──────────────────────────────
 * El hover de un botón lleno mezcla su fondo con su propio color de texto: un
 * botón oscuro con letra blanca se aclara y uno claro con letra oscura se
 * oscurece, y al cambiar la paleta sigue bien sin tocar nada.
 */
import type { StyleProperties } from '@webbuilder/style-engine'

export type InteractionStateName = 'hover' | 'pressed' | 'focus'
export type InteractionStates = Partial<Record<InteractionStateName, Partial<StyleProperties>>>

export type StateTransition = {
  /** Milisegundos. */
  duration?: number | undefined
  easing?: 'smooth' | 'ease' | 'ease-out' | 'linear' | undefined
}

export const DEFAULT_STATE_TRANSITION: { duration: number; easing: NonNullable<StateTransition['easing']> } = { duration: 200, easing: 'smooth' }

const EASING_CSS: Record<NonNullable<StateTransition['easing']>, string> = {
  smooth: 'cubic-bezier(.22,1,.36,1)',
  ease: 'ease',
  'ease-out': 'cubic-bezier(.16,1,.3,1)',
  linear: 'linear',
}

/*
 * Lo que se anima entre estados. Sólo propiedades baratas de interpolar —nada
 * de ancho ni alto, que moverían la página—. `translate` y `scale` van también
 * porque son las del efecto «Hover» de Animación, y sin ellas esta transición
 * le quitaría la suya.
 */
const TRANSITION_PROPERTIES = [
  'background-color', 'background', 'color', 'border-color', 'box-shadow', 'outline-color',
  'opacity', 'transform', 'filter', 'text-decoration-color', 'translate', 'scale',
]

/** La declaración `transition` de un estilo. */
export function stateTransitionCss(transition: StateTransition | undefined): string {
  const duration = Math.max(0, Math.min(2000, Math.round(transition?.duration ?? DEFAULT_STATE_TRANSITION.duration)))
  const easing = EASING_CSS[transition?.easing ?? DEFAULT_STATE_TRANSITION.easing]
  return TRANSITION_PROPERTIES.map((property) => `${property} ${duration}ms ${easing}`).join(', ')
}

const FOCUS_RING: Partial<StyleProperties> = {
  outline: '2px solid color-mix(in srgb, var(--wb-accent) 65%, transparent)',
  outlineOffset: '3px',
}

/** Si un valor de fondo pinta algo (no vacío, ni transparente, ni «none»). */
function isFilled(value: string | undefined): value is string {
  if (!value) return false
  const clean = value.trim().toLowerCase()
  return clean !== '' && clean !== 'transparent' && clean !== 'none' && !clean.startsWith('rgba(0, 0, 0, 0)') && !clean.startsWith('rgba(0,0,0,0)')
}

/**
 * Los estados por defecto de un botón, a partir de SUS estilos.
 *
 * - Lleno: el fondo se acerca un 12% a su propio color de texto.
 * - Contorno o fantasma: aparece un tinte suave de su color.
 * - Enlace: baja un poco la opacidad.
 * - Presionado: se hunde un 2%. Enfocado con teclado: un anillo del acento.
 */
export function defaultButtonStates(role: string | undefined, styles: Partial<StyleProperties> | undefined): InteractionStates {
  const record = (styles ?? {}) as Record<string, string | undefined>
  if (role === 'link') {
    return { hover: { opacity: '0.72' }, focus: FOCUS_RING }
  }
  const ink = record.color || 'currentColor'
  const fill = isFilled(record.backgroundColor) ? record.backgroundColor : isFilled(record.background) && !record.background.includes('gradient') && !record.background.includes('url(') ? record.background : undefined
  const hover: Partial<StyleProperties> = fill
    ? { backgroundColor: `color-mix(in srgb, ${fill} 88%, ${ink})`, background: `color-mix(in srgb, ${fill} 88%, ${ink})` }
    : isFilled(record.background)
      /* Degradado o imagen: no se puede mezclar un color con eso; se aclara. */
      ? { filter: 'brightness(1.06)' }
      : { backgroundColor: `color-mix(in srgb, ${ink} 8%, transparent)` }
  return {
    hover,
    pressed: { transform: 'scale(0.98)' },
    focus: FOCUS_RING,
  }
}

/** Los estados de un estilo global: los suyos, o los de su papel si no los define. */
export function componentStates(
  component: { role?: string | undefined; styles?: Partial<StyleProperties> | undefined; states?: InteractionStates | undefined } | undefined,
  kind: 'button' | 'card',
): InteractionStates {
  if (!component) return {}
  if (component.states) return component.states
  return kind === 'button' ? defaultButtonStates(component.role, component.styles) : {}
}

/** Los del estilo con los del elemento encima, propiedad a propiedad. */
export function mergeInteractionStates(base: InteractionStates, own: InteractionStates | undefined): InteractionStates {
  const out: InteractionStates = {}
  for (const state of ['hover', 'pressed', 'focus'] as const) {
    const merged = { ...(base[state] ?? {}), ...(own?.[state] ?? {}) }
    if (Object.keys(merged).length) out[state] = merged
  }
  return out
}

export function hasInteractionStates(states: InteractionStates): boolean {
  return Boolean(states.hover || states.pressed || states.focus)
}

/**
 * Los estilos de hover que se eligen con un clic en Estilo › Botones.
 *
 * `auto` es el de su papel (`defaultButtonStates`); el resto se calcula de los
 * estilos del propio botón, igual que él, para que sigan a la paleta.
 */
export const BUTTON_HOVER_PRESETS = ['auto', 'darken', 'lighten', 'lift', 'glow', 'none'] as const
export type ButtonHoverPreset = (typeof BUTTON_HOVER_PRESETS)[number]

export function buttonHoverPreset(preset: ButtonHoverPreset, role: string | undefined, styles: Partial<StyleProperties> | undefined): Partial<StyleProperties> | undefined {
  const record = (styles ?? {}) as Record<string, string | undefined>
  const fill = isFilled(record.backgroundColor) ? record.backgroundColor : isFilled(record.background) && !record.background.includes('gradient') && !record.background.includes('url(') ? record.background : undefined
  const tint = fill ?? record.color ?? 'var(--wb-primary)'
  switch (preset) {
    case 'auto': return defaultButtonStates(role, styles).hover
    case 'none': return undefined
    case 'darken': return fill
      ? { backgroundColor: `color-mix(in srgb, ${fill} 84%, #000)`, background: `color-mix(in srgb, ${fill} 84%, #000)` }
      : { filter: 'brightness(0.9)' }
    case 'lighten': return fill
      ? { backgroundColor: `color-mix(in srgb, ${fill} 82%, #fff)`, background: `color-mix(in srgb, ${fill} 82%, #fff)` }
      : { filter: 'brightness(1.1)' }
    case 'lift': return {
      transform: 'translateY(-2px)',
      boxShadow: `0 12px 24px -12px color-mix(in srgb, ${tint} 70%, transparent)`,
    }
    case 'glow': return {
      boxShadow: `0 0 0 4px color-mix(in srgb, ${tint} 22%, transparent), 0 10px 28px -10px color-mix(in srgb, ${tint} 65%, transparent)`,
    }
  }
}

/** Cuál de los estilos predefinidos es el hover de un botón, o `custom` si ninguno. */
export function detectButtonHoverPreset(component: { role?: string | undefined; styles?: Partial<StyleProperties> | undefined; states?: InteractionStates | undefined }): ButtonHoverPreset | 'custom' {
  if (!component.states) return 'auto'
  const hover = JSON.stringify(component.states.hover ?? null)
  for (const preset of BUTTON_HOVER_PRESETS) {
    if (JSON.stringify(buttonHoverPreset(preset, component.role, component.styles) ?? null) === hover) return preset
  }
  return 'custom'
}

/**
 * Atajos de estado para CUALQUIER elemento, en el inspector (modo avanzado).
 *
 * Son combinaciones de propiedades con nombre: se suman a lo que el estado ya
 * tenga y, pulsados otra vez, se quitan. Con `translate` y `scale` sueltos donde
 * se puede, para no pisar la rotación ni el desplazamiento del elemento.
 */
export const ELEMENT_STATE_PRESETS: Record<InteractionStateName, { id: string; styles: Partial<StyleProperties> }[]> = {
  hover: [
    { id: 'lift', styles: { transform: 'translateY(-3px)', boxShadow: '0 14px 30px -14px rgba(15,23,42,.35)' } },
    { id: 'grow', styles: { transform: 'scale(1.03)' } },
    { id: 'dim', styles: { opacity: '0.78' } },
    { id: 'shadow', styles: { boxShadow: '0 18px 40px -18px rgba(15,23,42,.4)' } },
    { id: 'underline', styles: { textDecoration: 'underline', textUnderlineOffset: '3px' } as Partial<StyleProperties> },
    { id: 'accent', styles: { color: 'var(--wb-accent)' } },
  ],
  pressed: [
    { id: 'sink', styles: { transform: 'scale(0.97)' } },
    { id: 'dim', styles: { opacity: '0.85' } },
  ],
  focus: [
    { id: 'ring', styles: { outline: '2px solid color-mix(in srgb, var(--wb-accent) 65%, transparent)', outlineOffset: '3px' } },
    { id: 'glow', styles: { boxShadow: '0 0 0 4px color-mix(in srgb, var(--wb-accent) 28%, transparent)' } },
  ],
}

/** Si un estado ya tiene puesto un atajo entero. */
export function hasStatePreset(state: Partial<StyleProperties> | undefined, preset: Partial<StyleProperties>): boolean {
  const current = (state ?? {}) as Record<string, string | undefined>
  return Object.entries(preset).every(([key, value]) => current[key] === value)
}
